import {useQueryClient} from '@tanstack/react-query';
import React, {ReactNode, createContext, useContext, useEffect, useState} from 'react';

import {marketT} from '@/lib/types/market.types';
import {getValidOrderProductsReturnT} from '@/lib/types/rpcFunctionReturns.types';
import {BasicDistributorT} from '@/lib/types/user.types';

import {SupabaseInstance} from '@/lib/utils';

import {useDataQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

type distributorOrderContextT = {
  error: string;
  isSubmitting: boolean;
  acknowledged: boolean;
  setAcknowledged: (acknowledged: boolean) => void;
  setAcknowledging: (acknowledged: boolean) => void;
  acknowledging: boolean;
  market: marketT | null;
  setMarket: (market: marketT | null) => void;
  distributor: BasicDistributorT | null;
  orderItems: {[key: number]: number};
  updateOrderItem: (productId: number, quantity: number) => void;
  resetOrderItems: () => void;
  handleSubmitOrder: () => void;
  showMarketSearch: boolean;
  setShowMarketSearch: (show: boolean) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  validOrderProducts: getValidOrderProductsReturnT['data'] | null;
  markets: marketT[];
  totals: getValidOrderProductsReturnT['counts'] | null;
};

const DistributorOrderDataContext = createContext<distributorOrderContextT | undefined>(undefined);

export const useCreateDistributorOrder = () => {
  const context = useContext(DistributorOrderDataContext);
  if (!context) {
    throw new Error('useCreateDistributorOrder must be used within a CreateDistributorOrderProvider');
  }
  return context;
};

interface OrderDataProviderProps {
  children: ReactNode;
  distributorId: number;
}

export const CreateDistributorOrderProvider: React.FC<OrderDataProviderProps> = ({children, distributorId}) => {
  const queryClient = useQueryClient();
  const {toast} = useNotifications();

  // Hooks
  const {
    userAccounts: {distributors},
  } = useAuth();

  // State
  const [market, setMarket] = useState<marketT | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [orderItems, setOrderItems] = useState<{[key: number]: number}>({});
  const [showMarketSearch, setShowMarketSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [validOrderProducts, setValidOrderProducts] = useState<getValidOrderProductsReturnT['data'] | null>(null);
  const [totals, setTotals] = useState<getValidOrderProductsReturnT['counts'] | null>(null);

  const distributor = distributors?.find(d => d.distributor_id === distributorId) || null;

  const invalidateQuery = async () => {
    await queryClient.invalidateQueries({queryKey: ['Order']});
    await queryClient.refetchQueries({queryKey: ['Order']});
  };

  // Markets Hook
  const {data: {data: marketsData} = {data: []}} = useDataQuery({
    table: 'Market_Distributor',
    columns: ['*', ['Market', ['*']]],
    limit: 10,
    searchTerm,
    searchColumn: 'Market.market_name',
    filters: [
      {
        column: 'fk_distributor_id',
        operator: 'eq',
        value: distributorId,
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    enabled: !!distributorId,
  });

  const markets = marketsData.map(md => md.Market);

  const {data: {data: distributorBrands} = {data: []}, isLoading: isDistributorBrandsLoading} = useDataQuery({
    table: 'Distributor_Brand',
    columns: ['*'],
    filters: [
      {
        column: 'fk_distributor_id',
        operator: 'eq',
        value: distributor?.distributor_id,
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    enabled: !!distributor && !!market,
  });

  useEffect(() => {
    if (!isDistributorBrandsLoading && distributor && market) {
      const newBrandIds = distributorBrands.map(db => db.fk_brand_id);
      setBrandIds(newBrandIds);
    }
  }, [isDistributorBrandsLoading, distributorBrands, distributor, market]);

  useEffect(() => {
    const fetchValidOrderProducts = async () => {
      if (market && brandIds.length > 0) {
        const {data, error} = await SupabaseInstance.supabase.rpc('get_valid_order_products', {
          brand_ids: brandIds,
          market_id: market.market_id,
        });

        if (error) {
          console.error('Error fetching valid order products:', error);
          toast.error('Failed to fetch products. Please try again.');
        } else {
          if (data && typeof data === 'object' && 'data' in data && 'counts' in data) {
            setValidOrderProducts(data.data as getValidOrderProductsReturnT['data']);
            setTotals(data.counts as getValidOrderProductsReturnT['counts']);
          }
        }
      } else {
        setValidOrderProducts(null);
        setTotals(null);
      }
    };

    fetchValidOrderProducts();
  }, [brandIds, market, toast]);

  const updateOrderItem = (productId: number, quantity: number) => {
    setOrderItems(prev => {
      const newItems = {...prev};
      if (quantity === 0) {
        delete newItems[productId];
      } else {
        newItems[productId] = quantity;
      }
      return newItems;
    });
  };

  const resetOrderItems = () => {
    setOrderItems({});
  };

  const handleSubmitOrder = async () => {
    if (!market || !distributor) {
      toast.error('Please select a market and distributor before submitting the order.');
      return;
    }
    const {data: user, error: userError} = await SupabaseInstance.supabase.auth.getUser();

    if (userError) {
      console.error('Error fetching user:', userError);
      toast.error('Failed to fetch user. Please try again.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const {data, error} = await SupabaseInstance.supabase.rpc('create_distributor_order', {
        p_order_items: orderItems,
        p_market_id: market.market_id,
        p_distributor_id: distributor.distributor_id,
        user_uid: user.user.id,
      });

      if (error) throw error;

      if (data) {
        toast.success('Order created successfully!');
        // Reset the form or navigate to a new page
        resetOrderItems();
        setMarket(null);
        invalidateQuery();
      } else {
        toast.error('Failed to create order. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting order in provider:', error);
      setError('An error occurred while submitting the order. Please try again.');
      toast.error({
        title: `Failed to submit order`,
        message: 'Please try again.',
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const value = {
    error,
    isSubmitting,
    acknowledging,
    setAcknowledging,
    acknowledged,
    setAcknowledged,
    market,
    setMarket,
    distributor,
    orderItems,
    updateOrderItem,
    resetOrderItems,
    handleSubmitOrder,
    showMarketSearch,
    setShowMarketSearch,
    searchTerm,
    setSearchTerm,
    validOrderProducts,
    markets,
    totals,
  };

  return <DistributorOrderDataContext.Provider value={value}>{children}</DistributorOrderDataContext.Provider>;
};
